import { useEffect, useState, useCallback } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  LoadingSkeleton,
  Modal,
} from '@nexora/design-system';
import { formatPercent } from '../../shared/utils/format';
import {
  usePaymentTerms,
  useCreatePaymentTerm,
  useUpdatePaymentTerm,
  useDeletePaymentTerm,
  INSTALLMENT_INTERVAL_LABEL,
  type PaymentTerm,
  type PaymentTermInput,
} from './usePaymentTerms';

/* ── Contratos reais (backend/.review-loop/reports/served-routes.json) ──
 * GET    /finance/payment-terms?isActive&q&page&pageSize → PaginatedResponseDto<PaymentTermResponseDto>
 * POST   /finance/payment-terms                  → PaymentTermResponseDto (PaymentTermInputDto)
 * PUT    /finance/payment-terms/:id              → PaymentTermResponseDto (PaymentTermUpdateInputDto)
 * DELETE /finance/payment-terms/:id              → { id, deleted }
 * (DTOs em backend/.../dto/treasury.dto.ts)
 */

const INTERVAL_OPTIONS = [
  { value: 'MONTHLY', label: 'Mensal' },
  { value: 'DAILY', label: 'Diário' },
  { value: 'WEEKLY', label: 'Semanal' },
  { value: 'CUSTOM_DAYS', label: 'Dias customizados' },
];

function formatPercentOrDash(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return formatPercent(v / 100);
}

export function PaymentTermsPage(): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<PaymentTerm | undefined>(undefined);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const termsQ = usePaymentTerms({ pageSize: 100 });
  const createMutation = useCreatePaymentTerm();
  const updateMutation = useUpdatePaymentTerm();
  const deleteMutation = useDeletePaymentTerm();

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = useCallback((m: string) => setToast(m), []);

  const handleSubmit = useCallback(
    async (input: PaymentTermInput) => {
      try {
        if (selected) {
          await updateMutation.mutateAsync({ id: selected.id, ...input });
          showToast('Prazo atualizado.');
        } else {
          await createMutation.mutateAsync(input);
          showToast('Prazo criado.');
        }
        setModalOpen(false);
        setSelected(undefined);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao salvar prazo.');
      }
    },
    [selected, createMutation, updateMutation, showToast],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteMutation.mutateAsync(id);
        showToast('Prazo excluído.');
        setDeleteConfirmId(null);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao excluir.');
        setDeleteConfirmId(null);
      }
    },
    [deleteMutation, showToast],
  );

  const data = termsQ.data;

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Configuração de Prazos e Parcelamento</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Regras de desconto à vista, juros/acréscimo e parcelamento para planos de pagamento.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => { setSelected(undefined); setModalOpen(true); }}
        >
          + Novo prazo
        </Button>
      </div>

      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Prazos de pagamento</h3>
          <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">{data?.total ?? 0} regras</span>
        </CardHeader>

        {termsQ.isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando prazos...</span>
          </div>
        )}

        {termsQ.error && !termsQ.isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar prazos</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>{(termsQ.error as Error).message}</p>
          </div>
        )}

        {!termsQ.isLoading && !termsQ.error && data && data.data.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>Nenhum prazo de pagamento cadastrado.</p>
            <Button variant="outline" size="sm" onClick={() => { setSelected(undefined); setModalOpen(true); }}>
              Criar primeiro prazo
            </Button>
          </div>
        )}

        {!termsQ.isLoading && !termsQ.error && data && data.data.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
              <thead>
                <tr>
                  {['Código', 'Nome', 'Desconto à vista', 'Juros mensal', 'Multa', 'Parcelamento', 'Ativo', 'Ações'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: h === 'Ações' ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.data.map((t) => (
                  <tr key={t.id} style={{ transition: 'background 0.15s ease' }} className="term-row">
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '12px' }}>{t.code}</span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 500 }}>{t.name}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>{formatPercentOrDash(t.discountPercent)}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>{formatPercentOrDash(t.interestRate)}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>{formatPercentOrDash(t.lateFeePercent)}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      {t.maxInstallments > 1
                        ? `até ${t.maxInstallments}x ${INSTALLMENT_INTERVAL_LABEL[t.installmentInterval] ?? t.installmentInterval}`
                        : 'À vista'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      <Badge variant={t.isActive ? 'success' : 'neutral'} size="sm" dot>
                        {t.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                        <Button variant="ghost" size="sm" onClick={() => { setSelected(t); setModalOpen(true); }}>Editar</Button>
                        {deleteConfirmId === t.id ? (
                          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.75rem', color: '#ba1a1a' }}>Excluir?</span>
                            <Button variant="danger" size="sm" loading={deleteMutation.isPending} onClick={() => handleDelete(t.id)}>Sim</Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(null)}>Não</Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(t.id)} style={{ color: '#ba1a1a' }}>Excluir</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <TermFormModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelected(undefined); }}
        term={selected}
        onSubmit={handleSubmit}
      />

      {toast && (
        <div role="alert" style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', padding: '0.75rem 1rem', borderRadius: '8px', background: '#d3f5d6', border: '1px solid #b6ebbb', color: '#2e7d32', fontSize: '0.875rem', fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10000, maxWidth: '400px' }}>
          {toast}
          <button onClick={() => setToast(null)} aria-label="Fechar" style={{ marginLeft: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem', padding: 0, lineHeight: 1 }}>&times;</button>
        </div>
      )}

      <style>{`
        .term-row:hover td { background: #eceef0; }
        .fin-form { display: flex; flex-direction: column; gap: 1rem; }
        .fin-form-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; }
        .fin-form-actions { display: flex; gap: 0.75rem; justify-content: flex-end; }
      `}</style>
    </div>
  );
}

function TermFormModal({ isOpen, onClose, term, onSubmit }: {
  isOpen: boolean;
  onClose: () => void;
  term?: PaymentTerm;
  onSubmit: (input: PaymentTermInput) => Promise<void>;
}) {
  const [code, setCode] = useState(term?.code ?? '');
  const [name, setName] = useState(term?.name ?? '');
  const [discountPercent, setDiscountPercent] = useState(term?.discountPercent?.toString() ?? '');
  const [interestRate, setInterestRate] = useState(term?.interestRate?.toString() ?? '');
  const [lateFeePercent, setLateFeePercent] = useState(term?.lateFeePercent?.toString() ?? '');
  const [maxInstallments, setMaxInstallments] = useState(term?.maxInstallments?.toString() ?? '1');
  const [installmentInterval, setInstallmentInterval] = useState(term?.installmentInterval ?? 'MONTHLY');
  const [installmentIntervalDays, setInstallmentIntervalDays] = useState(term?.installmentIntervalDays?.toString() ?? '');
  const [submitting, setSubmitting] = useState(false);

  const num = (s: string) => (s === '' ? undefined : parseFloat(s));
  const valid = !!code.trim() && !!name.trim();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit({
        code,
        name,
        ...(num(discountPercent) !== undefined ? { discountPercent: num(discountPercent) } : {}),
        ...(num(interestRate) !== undefined ? { interestRate: num(interestRate) } : {}),
        ...(num(lateFeePercent) !== undefined ? { lateFeePercent: num(lateFeePercent) } : {}),
        ...(num(maxInstallments) !== undefined ? { maxInstallments: Math.round(num(maxInstallments) ?? 1) } : {}),
        installmentInterval,
        ...(installmentInterval === 'CUSTOM_DAYS' && num(installmentIntervalDays) !== undefined
          ? { installmentIntervalDays: Math.round(num(installmentIntervalDays) ?? 30) }
          : {}),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={term ? `Editar prazo ${term.name}` : 'Novo prazo de pagamento'}>
      <form onSubmit={handleSubmit} className="fin-form">
        <div className="fin-form-row">
          <Input label="Código" value={code} onChange={(e) => setCode(e.target.value)} placeholder="EX.: 30-60-90" required disabled={!!term} />
          <Input label="Nome" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: 30/60/90 dias" required />
        </div>
        <div className="fin-form-row">
          <Input label="Desconto à vista (%)" type="number" step="0.01" min="0" max="100" value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)} />
          <Input label="Juros mensal (%)" type="number" step="0.01" min="0" max="100" value={interestRate} onChange={(e) => setInterestRate(e.target.value)} />
          <Input label="Multa por atraso (%)" type="number" step="0.01" min="0" max="100" value={lateFeePercent} onChange={(e) => setLateFeePercent(e.target.value)} />
        </div>
        <div className="fin-form-row">
          <Input label="Máximo de parcelas" type="number" min="1" value={maxInstallments} onChange={(e) => setMaxInstallments(e.target.value)} />
          <div className="fin-field">
            <label htmlFor="term-interval" className="fin-label">Intervalo</label>
            <select
              id="term-interval"
              className="fin-select"
              value={installmentInterval}
              onChange={(e) => setInstallmentInterval(e.target.value)}
            >
              {INTERVAL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {installmentInterval === 'CUSTOM_DAYS' && (
            <Input label="Dias entre parcelas" type="number" min="1" value={installmentIntervalDays} onChange={(e) => setInstallmentIntervalDays(e.target.value)} />
          )}
        </div>
        <div className="fin-form-actions">
          <Button variant="outline" type="button" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit" loading={submitting} disabled={!valid}>
            {term ? 'Atualizar' : 'Salvar regra'}
          </Button>
        </div>
      </form>

      <style>{`
        .fin-field { display: flex; flex-direction: column; gap: 0.25rem; }
        .fin-label { margin: 0; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #43474e; }
        .fin-select { padding: 0.5rem; background: #f7f9fb; border: 1px solid #c4c6cf; border-radius: 2px; font: inherit; font-size: 0.875rem; color: #191c1e; }
        .fin-select:focus { outline: none; border-color: #002045; box-shadow: 0 0 0 1px #002045; }
      `}</style>
    </Modal>
  );
}